<script>
  import Icon from "@iconify/svelte";
  import { onMount } from "svelte";
  import { t } from "../lib/i18n.svelte.js";
  import { api } from "../lib/api.svelte.js";
  import { addToast } from "../components/Toast.svelte";
  import Layout from "../components/Layout.svelte";
  import SlideOver from "../components/SlideOver.svelte";
  import Skeleton from "../components/Skeleton.svelte";
  import EmptyState from "../components/EmptyState.svelte";

  let sites = $state([]);
  let total = $state(0);
  let initialLoad = $state(true);
  let error = $state("");

  let search = $state("");
  let page = $state(1);
  let perPage = $state(15);
  let sortField = $state("title");
  let sortOrder = $state("asc");
  let viewMode = $state(localStorage.getItem("snapshot_sites_view") || "grid");

  let totalPages = $derived(Math.max(1, Math.ceil(total / perPage)));

  let pageNumbers = $derived.by(() => {
    const tp = totalPages;
    const cur = page;
    if (tp <= 7) return Array.from({ length: tp }, (_, i) => i + 1);
    const pages = [];
    pages.push(1);
    if (cur > 3) pages.push("...");
    for (let i = Math.max(2, cur - 1); i <= Math.min(tp - 1, cur + 1); i++) {
      pages.push(i);
    }
    if (cur < tp - 2) pages.push("...");
    pages.push(tp);
    return pages;
  });

  // Drawer state
  let drawerItem = $state(null);
  let editTitle = $state("");
  let editSlug = $state("");
  let editDescription = $state("");
  let saving = $state(false);
  let deleteConfirm = $state(false);
  let deleting = $state(false);

  function fullUrl(slug) {
    return slug ? slug + ".ui.localhost" : "-";
  }

  function setView(mode) {
    viewMode = mode;
    localStorage.setItem("snapshot_sites_view", mode);
  }

  function toggleSort(field) {
    if (sortField === field) {
      sortOrder = sortOrder === "asc" ? "desc" : "asc";
    } else {
      sortField = field;
      sortOrder = "asc";
    }
    page = 1;
    fetchData();
  }

  async function fetchData() {
    try {
      const q = encodeURIComponent(search);
      const res = await api.get(`/sites?page=${page}&per_page=${perPage}&q=${q}&sort=${sortField}&order=${sortOrder}`);
      sites = res.data || [];
      total = res.total || 0;
    } catch (e) {
      if (initialLoad) error = e.message || t("sites.failed_load");
    }
    initialLoad = false;
  }

  function handleSearch() {
    page = 1;
    fetchData();
  }

  function goPage(p) {
    page = p;
    fetchData();
  }

  function handlePerPage(val) {
    perPage = Number(val);
    page = 1;
    fetchData();
  }

  function openDrawer(site) {
    drawerItem = site;
    editTitle = site.title || "";
    editSlug = site.slug || "";
    editDescription = site.description || "";
    deleteConfirm = false;
  }

  function closeDrawer() {
    drawerItem = null;
    deleteConfirm = false;
  }

  async function handleSave() {
    if (!drawerItem) return;
    saving = true;
    try {
      await api.put(`/sites/${drawerItem.id}`, {
        title: editTitle,
        slug: editSlug,
        description: editDescription,
      });
      addToast(t("sites.updated"), "success");
      await fetchData();
      const updated = sites.find((s) => s.id === drawerItem.id);
      if (updated) openDrawer(updated);
    } catch (e) {
      addToast(e.message || t("sites.failed_save"), "error");
    }
    saving = false;
  }

  async function togglePublic() {
    if (!drawerItem) return;
    try {
      await api.put(`/sites/${drawerItem.id}`, { public: !drawerItem.public_at });
      await fetchData();
      const updated = sites.find((s) => s.id === drawerItem.id);
      if (updated) openDrawer(updated);
      addToast(updated?.public_at ? t("sites.now_public") : t("sites.now_private"), "success");
    } catch (e) {
      addToast(e.message || t("sites.failed_update"), "error");
    }
  }

  async function togglePublicInline(site) {
    try {
      await api.put(`/sites/${site.id}`, { public: !site.public_at });
      await fetchData();
      const updated = sites.find((s) => s.id === site.id);
      addToast(updated?.public_at ? t("sites.now_public") : t("sites.now_private"), "success");
    } catch (e) {
      addToast(e.message || t("sites.failed_update"), "error");
    }
  }

  async function confirmDelete() {
    if (!drawerItem) return;
    deleting = true;
    try {
      await api.del(`/sites/${drawerItem.id}`);
      addToast(t("sites.deleted"), "success");
      closeDrawer();
      await fetchData();
    } catch (e) {
      addToast(e.message || t("sites.failed_delete"), "error");
    }
    deleting = false;
  }

  function formatDate(d) {
    if (!d) return "-";
    return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  }

  onMount(() => {
    fetchData();
    const id = setInterval(() => {
      if (document.visibilityState !== "hidden") fetchData();
    }, 10000);
    return () => clearInterval(id);
  });
</script>

<Layout>
  <div class="flex gap-4">
    <div class="flex-1 min-w-0 {drawerItem ? 'lg:mr-[480px]' : ''}">
      <div class="ui-card p-5 sm:p-6">
        <!-- Header -->
        <div class="flex items-start gap-3 mb-6">
          <span class="ui-header-icon">
            <Icon icon="mdi:web" class="w-[18px] h-[18px]" />
          </span>
          <div class="flex-1">
            <h1 class="text-lg font-normal tracking-wide text-zinc-100">{t("sites.title")}</h1>
            <p class="mt-0.5 text-sm text-zinc-400">{t("sites.subtitle")}</p>
          </div>
        </div>

        <!-- Control bar -->
        <div class="flex items-center gap-3 flex-wrap mb-4">
          <!-- Search -->
          <div class="flex-1 min-w-[180px]">
            <input
              type="text"
              class="w-full h-9 px-3 bg-black/20 border border-white/10 rounded text-sm text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-white/25"
              placeholder={t("common.search_placeholder")}
              bind:value={search}
              oninput={handleSearch}
            />
          </div>

          <!-- Grid/List toggle -->
          <div class="flex items-center gap-0.5 bg-black/20 border border-white/10 rounded p-0.5">
            <button
              class="h-8 w-8 flex items-center justify-center rounded transition-colors {viewMode === 'grid' ? 'bg-white/10 text-white' : 'text-zinc-500 hover:text-zinc-300'}"
              onclick={() => setView("grid")}
              title={t("sites.grid_view")}
            >
              <Icon icon="mdi:view-grid-outline" class="w-4 h-4" />
            </button>
            <button
              class="h-8 w-8 flex items-center justify-center rounded transition-colors {viewMode === 'list' ? 'bg-white/10 text-white' : 'text-zinc-500 hover:text-zinc-300'}"
              onclick={() => setView("list")}
              title={t("sites.list_view")}
            >
              <Icon icon="mdi:view-list-outline" class="w-4 h-4" />
            </button>
          </div>

          <!-- Per page -->
          <select
            class="h-9 px-2 bg-black/20 border border-white/10 rounded text-sm text-zinc-300 focus:outline-none focus:border-white/25 cursor-pointer"
            value={perPage}
            onchange={(e) => handlePerPage(e.target.value)}
          >
            <option value={15}>15</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>

          <!-- Refresh -->
          <button
            class="h-9 w-9 flex items-center justify-center bg-black/20 border border-white/10 rounded text-zinc-400 hover:text-white hover:border-white/25 transition-colors"
            onclick={fetchData}
            title={t("common.refresh")}
          >
            <Icon icon="mdi:refresh" class="w-4 h-4" />
          </button>

          <!-- Pagination -->
          <div class="flex items-center gap-1">
            <button
              class="h-8 w-8 flex items-center justify-center rounded text-sm text-zinc-400 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              disabled={page <= 1}
              onclick={() => goPage(page - 1)}
            >&lsaquo;</button>
            {#each pageNumbers as p}
              {#if p === "..."}
                <span class="h-8 w-8 flex items-center justify-center text-sm text-zinc-600">&hellip;</span>
              {:else}
                <button
                  class="h-8 w-8 flex items-center justify-center rounded text-sm transition-colors {p === page ? 'bg-white/10 text-white font-medium' : 'text-zinc-400 hover:text-white hover:bg-white/10'}"
                  onclick={() => goPage(p)}
                >{p}</button>
              {/if}
            {/each}
            <button
              class="h-8 w-8 flex items-center justify-center rounded text-sm text-zinc-400 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              disabled={page >= totalPages}
              onclick={() => goPage(page + 1)}
            >&rsaquo;</button>
          </div>
        </div>

        {#if error}
          <div class="px-3 py-2 rounded border border-rose-500/30 bg-rose-500/10 text-sm text-rose-200 mb-4">
            {error}
          </div>
        {/if}

        <!-- Grid view -->
        {#if viewMode === "grid"}
          <!-- Grid sort control -->
          <div class="flex items-center gap-2 text-xs text-zinc-500 mb-3">
            <span>{t("sites.sort")}:</span>
            <button
              class="hover:text-zinc-300 transition-colors {sortField === 'title' ? 'text-zinc-200' : ''}"
              onclick={() => toggleSort("title")}
            >
              {t("sites.name")}
              {#if sortField === "title"}
                <Icon icon={sortOrder === "asc" ? "mdi:arrow-up" : "mdi:arrow-down"} class="w-3 h-3 inline" />
              {/if}
            </button>
            <button
              class="hover:text-zinc-300 transition-colors {sortField === 'created_at' ? 'text-zinc-200' : ''}"
              onclick={() => toggleSort("created_at")}
            >
              {t("sites.date")}
              {#if sortField === "created_at"}
                <Icon icon={sortOrder === "asc" ? "mdi:arrow-up" : "mdi:arrow-down"} class="w-3 h-3 inline" />
              {/if}
            </button>
          </div>

          {#if initialLoad}
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {#each Array(6) as _}
                <div class="p-4 rounded border border-white/10 bg-white/[0.02]">
                  <div class="space-y-3">
                    <div class="w-full bg-white/[0.04] rounded animate-pulse" style="aspect-ratio:16/10"></div>
                    <Skeleton lines={2} />
                  </div>
                </div>
              {/each}
            </div>
          {:else if sites.length === 0}
            <EmptyState message={t("sites.no_sites")} />
          {:else}
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {#each sites as site (site.id)}
                <button
                  type="button"
                  class="text-left w-full"
                  onclick={() => openDrawer(site)}
                >
                  <div class="p-4 rounded border border-white/10 bg-white/[0.02] hover:border-white/20 transition-colors cursor-pointer relative">
                    <div class="absolute top-2 right-2 z-10">
                      <Icon icon="mdi:circle" class="w-3 h-3 {site.public_at ? 'text-green-500' : 'text-zinc-600'}" />
                    </div>
                    {#if site.screenshot_url}
                      <img
                        src={site.screenshot_url}
                        alt={site.title || site.slug}
                        class="w-full rounded mb-3 object-cover"
                        style="aspect-ratio:16/10"
                      />
                    {:else}
                      <div class="w-full mb-3 bg-white/[0.04] rounded flex items-center justify-center" style="aspect-ratio:16/10">
                        <Icon icon="mdi:web" class="w-8 h-8 text-zinc-600" />
                      </div>
                    {/if}
                    <h3 class="text-sm font-semibold text-white truncate">{site.title || site.slug || "-"}</h3>
                    {#if site.slug}
                      <p class="text-xs text-zinc-500 truncate mt-0.5">{fullUrl(site.slug)}</p>
                    {/if}
                    {#if site.description}
                      <p class="text-xs text-zinc-400 mt-1 line-clamp-2">{site.description}</p>
                    {/if}
                  </div>
                </button>
              {/each}
            </div>
          {/if}
        {:else}
          <!-- List view -->
          {#if initialLoad}
            <Skeleton lines={8} />
          {:else if sites.length === 0}
            <EmptyState message={t("sites.no_sites")} />
          {:else}
            <!-- Mobile: card layout -->
            <div class="md:hidden space-y-2">
              {#each sites as site (site.id)}
                <button
                  type="button"
                  class="w-full text-left p-4 rounded-lg border border-white/5 bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
                  onclick={() => openDrawer(site)}
                >
                  <div class="flex items-center justify-between mb-2">
                    <span class="inline-flex items-center gap-2 text-white font-medium text-sm truncate mr-2">
                      <Icon icon="mdi:web" class="w-4 h-4 text-zinc-600 shrink-0" />
                      {site.title || "-"}
                    </span>
                    <span class="inline-flex items-center gap-1.5 text-xs shrink-0 {site.public_at ? 'text-green-500' : 'text-zinc-500'}">
                      <Icon icon="mdi:circle" class="w-2.5 h-2.5" />
                      <span>{site.public_at ? t("sites.public") : t("sites.private")}</span>
                    </span>
                  </div>
                  <div class="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    <span class="text-zinc-500">{t("sites.url")}</span>
                    <span class="text-zinc-400 truncate">{fullUrl(site.slug)}</span>
                    {#if site.description}
                      <span class="text-zinc-500">{t("sites.description")}</span>
                      <span class="text-zinc-400 truncate">{site.description}</span>
                    {/if}
                    <span class="text-zinc-500">{t("sites.created")}</span>
                    <span class="text-zinc-400">{formatDate(site.created_at)}</span>
                  </div>
                </button>
              {/each}
            </div>

            <!-- Desktop: table layout -->
            <div class="hidden md:block overflow-x-auto">
              <table class="w-full text-sm">
                <thead>
                  <tr class="border-b border-white/10">
                    <th class="text-left py-3 px-4 text-xs uppercase tracking-wide text-zinc-500 font-medium cursor-pointer hover:text-zinc-300" onclick={() => toggleSort("title")}>
                      {t("common.title")}
                      {#if sortField === "title"}
                        <Icon icon={sortOrder === "asc" ? "mdi:arrow-up" : "mdi:arrow-down"} class="w-3 h-3 inline" />
                      {/if}
                    </th>
                    <th class="text-left py-3 px-4 text-xs uppercase tracking-wide text-zinc-500 font-medium">{t("sites.slug")}</th>
                    <th class="text-left py-3 px-4 text-xs uppercase tracking-wide text-zinc-500 font-medium">{t("sites.description")}</th>
                    <th class="text-center py-3 px-4 text-xs uppercase tracking-wide text-zinc-500 font-medium">{t("sites.public")}</th>
                    <th class="text-left py-3 px-4 text-xs uppercase tracking-wide text-zinc-500 font-medium cursor-pointer hover:text-zinc-300" onclick={() => toggleSort("created_at")}>
                      {t("sites.created")}
                      {#if sortField === "created_at"}
                        <Icon icon={sortOrder === "asc" ? "mdi:arrow-up" : "mdi:arrow-down"} class="w-3 h-3 inline" />
                      {/if}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {#each sites as site (site.id)}
                    <tr
                      class="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer transition-colors"
                      onclick={() => openDrawer(site)}
                    >
                      <td class="py-3 px-4 text-white font-medium"><span class="inline-flex items-center gap-2"><Icon icon="mdi:web" class="w-4 h-4 text-zinc-600 shrink-0" />{site.title || "-"}</span></td>
                      <td class="py-3 px-4 text-zinc-400">{fullUrl(site.slug)}</td>
                      <td class="py-3 px-4 text-zinc-400 max-w-[240px] truncate">{site.description || "-"}</td>
                      <td class="py-3 px-4 text-center">
                        <button
                          class="w-10 h-5 rounded-full transition-colors relative inline-block {site.public_at ? 'bg-green-500' : 'bg-zinc-700'}"
                          onclick={(e) => { e.stopPropagation(); togglePublicInline(site); }}
                          title={site.public_at ? "Make private" : "Make public"}
                        >
                          <div class="w-4 h-4 rounded-full bg-white absolute top-0.5 transition-all {site.public_at ? 'left-5' : 'left-0.5'}"></div>
                        </button>
                      </td>
                      <td class="py-3 px-4 text-zinc-500 whitespace-nowrap">{formatDate(site.created_at)}</td>
                    </tr>
                  {/each}
                </tbody>
              </table>
            </div>
          {/if}
        {/if}
      </div>
    </div>
  </div>

  <!-- Detail drawer -->
  {#if drawerItem}
    <SlideOver onclose={closeDrawer} title={drawerItem.title || drawerItem.slug || t("nav.sites")} width="480px">
      <p class="text-sm text-zinc-400 mb-6">{fullUrl(drawerItem.slug)} &middot; {formatDate(drawerItem.created_at)}</p>

      <!-- Public toggle -->
      <div class="flex items-center justify-between mb-6 p-3 rounded border border-white/[0.06] bg-white/[0.02]">
        <div>
          <span class="text-sm text-zinc-200">{t("sites.public_access")}</span>
          <p class="text-xs text-zinc-500">{drawerItem.public_at ? t("sites.visible_to_anyone") : t("sites.requires_invite")}</p>
        </div>
        <button
          class="w-10 h-5 rounded-full transition-colors relative {drawerItem.public_at ? 'bg-green-500' : 'bg-zinc-700'}"
          onclick={togglePublic}
        >
          <div class="w-4 h-4 rounded-full bg-white absolute top-0.5 transition-all {drawerItem.public_at ? 'left-5' : 'left-0.5'}"></div>
        </button>
      </div>

      <!-- Edit form -->
      <div class="space-y-4">
        <div>
          <label class="block text-xs text-zinc-300 mb-1.5">{t("common.title")}</label>
          <input type="text" class="w-full h-9 px-3 bg-black/20 border border-white/10 rounded text-sm text-white focus:outline-none focus:border-white/25" bind:value={editTitle} />
        </div>
        <div>
          <label class="block text-xs text-zinc-300 mb-1.5">{t("sites.slug")}</label>
          <input type="text" class="w-full h-9 px-3 bg-black/20 border border-white/10 rounded text-sm text-white focus:outline-none focus:border-white/25" bind:value={editSlug} />
        </div>
        <div>
          <label class="block text-xs text-zinc-300 mb-1.5">{t("sites.description")}</label>
          <textarea class="w-full h-20 px-3 py-2 bg-black/20 border border-white/10 rounded text-sm text-white focus:outline-none focus:border-white/25 resize-none" bind:value={editDescription}></textarea>
        </div>

        <div class="flex gap-2 pt-2">
          <button class="ui-btn-accent flex-1 !py-2 !text-sm !gap-2 !rounded" onclick={handleSave} disabled={saving}>
            <Icon icon="mdi:content-save-outline" class="w-4 h-4" />
            {saving ? t("sites.saving") : t("common.save")}
          </button>
          <button class="flex-1 inline-flex items-center justify-center gap-2 h-9 rounded bg-rose-500 text-white text-sm font-medium hover:bg-rose-400 transition-colors" onclick={() => deleteConfirm = true}>
            <Icon icon="mdi:trash-can-outline" class="w-4 h-4" />
            {t("common.delete")}
          </button>
        </div>
      </div>
    </SlideOver>
  {/if}

  <!-- Delete confirmation -->
  {#if deleteConfirm}
    <div class="fixed inset-0 z-[200] flex items-center justify-center">
      <button class="absolute inset-0 bg-black/60" onclick={() => (deleteConfirm = false)} aria-label="Cancel delete"></button>
      <div class="relative ui-card p-6 max-w-sm border border-rose-500/30 bg-rose-500/5">
        <h3 class="text-base font-semibold text-white mb-2">{t("sites.confirm_delete")}</h3>
        <p class="text-sm text-zinc-400 mb-4">{t("sites.delete_warning")}</p>
        <div class="flex justify-end gap-2">
          <button class="ui-btn" onclick={() => (deleteConfirm = false)}>{t("common.cancel")}</button>
          <button
            class="ui-btn bg-rose-600 border-rose-500 text-white hover:bg-rose-500"
            onclick={confirmDelete}
            disabled={deleting}
          >
            {deleting ? t("sites.deleting") : t("common.delete")}
          </button>
        </div>
      </div>
    </div>
  {/if}
</Layout>
